/**
 * `F5` · `T-53` — El campo único, que es toda la pantalla de trabajo.
 *
 * `Flujo-de-mostrador`: **un campo de texto con el foco puesto, siempre**, que
 * acepta tres entradas sin que la cajera cambie de modo nunca:
 *
 *   · el código que el comensal enseña, tecleado,
 *   · el mismo código, **escrito por el lector USB** —que es un teclado—,
 *   · o su teléfono completo.
 *
 * ── Por qué el foco es una decisión y no un detalle ───────────────────────
 * El lector USB «escribe» donde esté el cursor. Si tras cada cliente hubiera que
 * volver a pinchar el campo, se pierde el segundo que costó comprarlo — y es el
 * criterio observable de la tarea. Por eso se devuelve el foco tras **cada**
 * búsqueda, acierte o falle, y tras cerrar una tarjeta.
 *
 * Quién decide qué es lo que llegó no vive aquí: vive en
 * `interpretarEntradaDeMostrador`, en `@fialrest/shared`, y lo comparte con la
 * API. Dos criterios para lo mismo es cómo acaban la pantalla y el motor
 * discrepando sobre si algo era un teléfono.
 */
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import type { TarjetaEnLaCaja } from '@fialrest/shared'
import { ErrorDeApi } from '@fialrest/cliente'
import { buscarCliente } from './api.js'
import { Tarjeta } from './Tarjeta.js'

interface Props {
  /** Se avisa hacia arriba de cada gesto: los cinco minutos son de inactividad. */
  readonly alTrabajar: () => void
  /** El turno caducó del lado del servidor: hay que volver a pedir el PIN. */
  readonly alPerderElTurno: () => void
}

export function Buscar({ alTrabajar, alPerderElTurno }: Props): ReactNode {
  const [entrada, setEntrada] = useState('')
  const [tarjeta, setTarjeta] = useState<TarjetaEnLaCaja | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [buscando, setBuscando] = useState(false)
  //  Lo que se tecleó cuando la encontró. Se guarda porque el canje lo gasta, y
  //  para entonces el campo ya está vacío esperando al siguiente cliente.
  const [codigoUsado, setCodigoUsado] = useState<string | null>(null)
  const campo = useRef<HTMLInputElement>(null)

  //  El foco vuelve al campo cada vez que la pantalla cambia de estado. Es lo
  //  que hace que el lector USB no necesite que nadie pinche nada.
  useEffect(() => { campo.current?.focus() }, [tarjeta, error, buscando])

  const buscar = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    const texto = entrada.trim()
    if (texto === '' || buscando) return
    alTrabajar()
    setBuscando(true)
    setError(null)
    setTarjeta(null)
    try {
      const encontrada = await buscarCliente(texto)
      setTarjeta(encontrada)
      setCodigoUsado(encontrada.comoLaEncontre === 'codigo' ? texto.trim().toUpperCase() : null)
      setEntrada('')
    } catch (falla) {
      if (falla instanceof ErrorDeApi) {
        //  401 es el único que no se enseña: el turno caducó y la pantalla
        //  vuelve al PIN. Enseñar «no autorizado» a una cajera no le dice qué
        //  hacer; volver a su teclado numérico sí.
        if (falla.esSinSesion) { alPerderElTurno(); return }
        setError(falla.message)
      } else {
        setError('No se pudo buscar. Revisa la conexión e inténtalo otra vez.')
      }
      //  La entrada NO se borra al fallar: si tecleó mal un dígito del teléfono,
      //  borrárselo la obliga a pedir el número otra vez delante de la persona.
    } finally {
      setBuscando(false)
    }
  }

  return (
    <section className="caja-buscar">
      <form className="caja-buscar__forma" onSubmit={(e) => { void buscar(e) }}>
        <label className="caja-buscar__etiqueta" htmlFor="entrada">
          Código o teléfono
        </label>
        <input
          id="entrada"
          ref={campo}
          className="caja-buscar__campo"
          //  `type="text"` y no `tel`: por aquí entran letras. Y sin
          //  autocompletado ni corrección: el lector manda caracteres sueltos y
          //  el navegador podría «arreglarlos».
          type="text"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="characters"
          spellCheck={false}
          value={entrada}
          autoFocus
          onChange={(e) => { setEntrada(e.target.value); alTrabajar() }}
        />
        <button className="caja-buscar__boton" type="submit" disabled={buscando}>
          {buscando ? 'Buscando…' : 'Buscar'}
        </button>
      </form>

      {error === null ? null : <p className="caja-buscar__error" role="alert">{error}</p>}

      {tarjeta === null ? null : (
        <Tarjeta
          tarjeta={tarjeta}
          //  El código sólo viaja si fue el código lo que la encontró — lo
          //  decide `setCodigoUsado` al encontrarla, y aquí no se vuelve a
          //  preguntar: dos sitios decidiendo lo mismo es cómo acaban
          //  contestando distinto. Si llegó por teléfono no hay ninguno que
          //  gastar, y el canje quedará marcado —`R-09`— que es lo que se quiere.
          codigo={codigoUsado}
          alActuar={(ficha) => { setTarjeta(ficha); alTrabajar() }}
          alCerrar={() => { setTarjeta(null); setCodigoUsado(null); alTrabajar() }}
        />
      )}
    </section>
  )
}
